
import { Vue, Component, Prop, Watch } from "vue-property-decorator";
import { 
  validateAndFormatPhoneNumber, 
  formatUSPhoneNumber, 
  cleanPhoneNumber,
  validatePhoneNumberBasic,
  PHONE_PATTERNS 
} from "@/utils/phoneValidation";

export type PhoneFormat = 'us';
export type PhoneInputSize = 'sm' | 'md' | 'lg';
export type PhoneInputVariant = 'default' | 'outline' | 'filled';

@Component
export default class StandardizedPhoneInput extends Vue {
  @Prop({ type: String, default: '' }) value!: string;
  @Prop({ type: String, default: 'us' }) format!: PhoneFormat;
  @Prop({ type: Boolean, default: false }) disabled!: boolean;
  @Prop({ type: Boolean, default: false }) required!: boolean;
  @Prop({ type: String, default: 'md' }) size!: PhoneInputSize;
  @Prop({ type: String, default: 'outline' }) variant!: PhoneInputVariant;
  @Prop({ type: String, default: '' }) placeholder!: string;
  @Prop({ type: String, default: '' }) helperText!: string;
  @Prop({ type: Boolean, default: false }) showHelperText!: boolean;
  @Prop({ type: Boolean, default: false }) invalid!: boolean;
  @Prop({ type: String, default: '' }) errorMessage!: string;
  @Prop({ type: Boolean, default: true }) showError!: boolean;
  @Prop({ type: String, default: '' }) inputId!: string;
  @Prop({ type: String, default: '' }) customClass!: string;
  @Prop({ type: Boolean, default: true }) formatOnBlur!: boolean;
  @Prop({ type: Boolean, default: true }) validateOnInput!: boolean;

  internalValue = '';
  isFocused = false;
  lastValidValue = '';

  created() {
    this.internalValue = this.value;
  }

  @Watch('value')
  onValueChanged(newValue: string) {
    if (newValue !== this.internalValue) {
      this.internalValue = newValue;
    }
  }

  get currentFormat(): 'us' {
    return 'us';
  }

  get currentPlaceholder(): string {
    if (this.placeholder) return this.placeholder;
    return '(123) 456-7890';
  }

  get maxLength(): number {
    return PHONE_PATTERNS.US.MAX_LENGTH;
  }

  get formattedValue(): string {
    if (!this.internalValue) return '';
    
    if (this.isFocused && !this.formatOnBlur) {
      return this.internalValue;
    }

    const result = validateAndFormatPhoneNumber(this.internalValue, 'us');
    return result.formatted || this.internalValue;
  }

  get inputClasses(): string {
    const baseClasses = 'phone-input';
    const sizeClasses = {
      sm: 'px-3 py-1.5 text-sm',
      md: 'px-3 py-2 text-base',
      lg: 'px-4 py-3 text-lg'
    };
    const variantClasses = {
      default: 'border border-gray-300 rounded-md',
      outline: 'border border-gray-300 rounded-md bg-white',
      filled: 'border-0 bg-gray-100 rounded-md'
    };
    const stateClasses = {
      normal: 'border-gray-300 focus:border-blue-500',
      invalid: 'border-red-500 focus:border-red-500',
      disabled: 'bg-gray-100 text-gray-500 cursor-not-allowed'
    };

    let classes = [baseClasses, sizeClasses[this.size], variantClasses[this.variant]];
    
    if (this.disabled) {
      classes.push(stateClasses.disabled);
    } else if (this.invalid) {
      classes.push(stateClasses.invalid);
    } else {
      classes.push(stateClasses.normal);
    }


    if (this.customClass) {
      classes.push(this.customClass);
    }

    return classes.join(' ');
  }


  handleInput(event: Event): void {
    const target = event.target as HTMLInputElement;
    let inputValue = target.value;

    // Remove any formatting to get raw input
    const cleaned = cleanPhoneNumber(inputValue);
    
    // Validate length limits - US: 10 digits
    if (cleaned.length > PHONE_PATTERNS.US.DIGITS_MAX) {
      // Prevent input if too long
      target.value = this.formattedValue;
      return;
    }

    this.internalValue = cleaned;
    
    if (this.validateOnInput) {
      this.validateValue();
    }
    
    this.emitValue();
  }

  handleBlur(): void {
    this.isFocused = false;
    
    if (this.formatOnBlur && this.internalValue) {
      const result = validateAndFormatPhoneNumber(this.internalValue, 'us');
      if (result.isValid) {
        this.internalValue = result.formatted;
        this.lastValidValue = this.internalValue;
      }
    }
    
    this.validateValue();
    this.emitValue();
    this.$emit('blur', this.internalValue);
  }

  handleFocus(): void {
    this.isFocused = true;
    this.$emit('focus', this.internalValue);
  }

  handlePaste(event: ClipboardEvent): void {
    event.preventDefault();
    const pastedText = event.clipboardData?.getData('text') || '';
    let cleaned = cleanPhoneNumber(pastedText);
    
    // Limit to 10 digits
    cleaned = cleaned.slice(0, PHONE_PATTERNS.US.DIGITS_MAX);
    
    this.internalValue = cleaned;
    this.validateValue();
    this.emitValue();
  }

  validateValue(): void {
    if (!this.validateOnInput && !this.showError) return;
    
    const validation = validatePhoneNumberBasic(this.internalValue);
    this.$emit('validation', {
      isValid: validation.valid,
      message: validation.message,
      value: this.internalValue
    });
  }

  emitValue(): void {
    this.$emit('input', this.internalValue);
    this.$emit('change', this.internalValue);
  }

  // Public methods for parent components
  public focus(): void {
    (this.$refs.phoneInput as HTMLInputElement).focus();
  }

  public blur(): void {
    (this.$refs.phoneInput as HTMLInputElement).blur();
  }

  public validate(): { isValid: boolean; message?: string } {
    const result = validatePhoneNumberBasic(this.internalValue);
    return { isValid: result.valid, message: result.message };
  }

  public getFormattedValue(): string {
    const result = validateAndFormatPhoneNumber(this.internalValue, 'us');
    return result.formatted;
  }

  public getRawValue(): string {
    return cleanPhoneNumber(this.internalValue);
  }
}
